import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import useStore from "../../../context/useStore";
import { ACCUEIL_OPERATEUR } from "../../../helpers/perimetreOperateur";
import { estUtilisateurOperateur } from "../../../helpers/rolesManager";
import roleConfig from "../../common/layout/roleConfig";
import Loader from "../../common/Loader";
import Control from "../../common/Control";
import ModaleLocale from "../../common/CustomModal/ModaleLocale";
import { Pill, EmptyState } from "../../common/DataList";
import { FactList, FicheHeader, TabPanel, Tabs, useFicheTabs } from "../../common/Fiche";
import EncartMiseEnService from "./EncartMiseEnService";
import OngletInformations from "./OngletInformations";
import OngletSites from "./OngletSites";
import OngletUtilisateurs from "./OngletUtilisateurs";
import OngletQuotas from "./OngletQuotas";
import { estDesactive } from "./comptesOperateur";
import {
  desactiverOperateur,
  listerComptesDeOperateur,
  mettreEnServiceOperateur,
  reactiverOperateur,
  supprimerOperateur,
  voirOperateur,
} from "./operateursApi";
import {
  REGIMES_ACCOMPAGNEMENT,
  STATUTS_OPERATEUR,
  libelleStatut,
} from "./libellesOperateur";
import "./operateurs.scss";

// ============================================================
// Fiche opérateur (CE-1404, maquette 10) sur le kit Fiche : identité et
// pastilles, encart de mise en service, onglets Informations, Sites et
// calendriers, Utilisateurs (CE-1407, opérateur avec régime) et Communes et
// quotas (CE-1369, opérateur avec régime ; saisie par la Caf, lecture par
// l'opérateur connecté depuis CE-1410). Pas d'onglet Historique (pas de source API, à cadrer à
// part). Actions de la Caf : mettre en service, désactiver (motif
// obligatoire), réactiver, supprimer (si aucun site n'est cité ; sinon
// l'écran propose la désactivation). Le rattachement n'est pas affiché :
// toute structure est rattachée à la Caf.
// Vue de l'opérateur connecté (`vueOperateur`, route /ma-structure, ou
// /operateurs/:id de sa propre structure) : « Ma structure », sans retour,
// sans action d'en-tête ni de mise en service, Informations et Sites en
// lecture ; l'onglet Utilisateurs n'offre d'actions qu'à l'administrateur.
// Une autre structure est refusée sans appel à l'API.
// ============================================================

const ONGLETS = [
  { id: "informations", label: "Informations" },
  { id: "sites", label: "Sites et calendriers" },
  { id: "utilisateurs", label: "Utilisateurs" },
  { id: "quotas", label: "Communes et quotas" },
];

const OperateurDetail = (props) => {
  const [state] = useStore();
  const utilisateur = (state.auth && state.auth.user) || {};
  const params = (props.match && props.match.params) || {};
  const compteOperateur = estUtilisateurOperateur(utilisateur);
  const vueOperateur = Boolean(props.vueOperateur) || (compteOperateur && (!params.id || params.id === utilisateur.operateur));
  const operateurId = vueOperateur ? utilisateur.operateur : params.id;
  const structureEtrangere = compteOperateur && !vueOperateur;
  const ongletDemande = params.onglet || null;

  const [fiche, setFiche] = useState(null);
  const [comptes, setComptes] = useState(null);
  const [erreurComptes, setErreurComptes] = useState(null);
  const [introuvable, setIntrouvable] = useState(false);
  const [erreurChargement, setErreurChargement] = useState(null);
  const refTitre = useRef(null);

  const [refusMiseEnService, setRefusMiseEnService] = useState(null);
  const [actionEnCours, setActionEnCours] = useState(false);
  const [modaleDesactivation, setModaleDesactivation] = useState(null); // { motif, erreurs, refus }
  const [modaleSuppression, setModaleSuppression] = useState(null); // { refus, cite }

  // Introuvable seulement sur un 404 ; toute autre erreur (réseau, 500) se
  // dit comme telle, avec « Réessayer » tant que la fiche n'est pas chargée.
  const chargerFiche = () => {
    setErreurChargement(null);
    voirOperateur(operateurId).then(
      (ficheChargee) => {
        setFiche(ficheChargee);
        if ((ficheChargee.regimes || []).length) chargerComptes();
      },
      (erreur) => {
        if (erreur && erreur.statut === 404) {
          setIntrouvable(true);
          return;
        }
        const message = (erreur && erreur.message) || "Une erreur est survenue";
        if (fiche) toast.error(message);
        else setErreurChargement(message);
      }
    );
  };

  // Les comptes servent à l'onglet Utilisateurs et à l'étape « administrateur
  // activé » de l'encart ; une erreur de chargement laisse l'étape à venir.
  // Premier chargement en échec : l'onglet le dit, avec « Réessayer » ;
  // rechargement en échec après une action : la liste reste, un message le dit.
  const chargerComptes = () => {
    setErreurComptes(null);
    listerComptesDeOperateur(operateurId).then(setComptes, (erreur) => {
      const message = (erreur && erreur.message) || "Une erreur est survenue";
      if (comptes) toast.error(message);
      else setErreurComptes(message);
    });
  };

  // Un régime ajouté depuis l'onglet Informations fait apparaître l'onglet
  // Utilisateurs : ses comptes se chargent sans attendre un rechargement.
  const ficheModifiee = (ficheAJour) => {
    setFiche(ficheAJour);
    if ((ficheAJour.regimes || []).length && comptes === null) chargerComptes();
  };

  // Désactiver et Réactiver retirent de l'en-tête le bouton qui vient d'être
  // activé : le focus irait sur <body>, il revient sur le titre de la fiche.
  const ramenerFocusSurTitre = () => {
    if (refTitre.current) refTitre.current.focus();
  };

  useEffect(() => {
    if (structureEtrangere || !operateurId) return;
    chargerFiche();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operateurId, structureEtrangere]);

  const avecRegime = Boolean(fiche && (fiche.regimes || []).length);
  // Seuls les profils qui gèrent les opérateurs (operator update:any côté API) lisent les quotas.
  const gereLesOperateurs = !(roleConfig[utilisateur.role] || []).includes("crud_operateurs");
  const nombreComptesVisibles = Array.isArray(comptes) ? comptes.filter((c) => !estDesactive(c)).length : null;
  const onglets = ONGLETS.map((o) => {
    if (o.id === "sites" && fiche) return { ...o, badge: fiche.nombreSites };
    if (o.id === "utilisateurs") {
      return { ...o, hidden: Boolean(fiche) && !avecRegime, badge: nombreComptesVisibles === null ? undefined : nombreComptesVisibles };
    }
    // Les quotas se saisissent à la Caf et se lisent par l'opérateur connecté (CE-1410) ; pas
    // d'onglet pour un profil Caf qui ne gère pas les opérateurs (agent, directeur…), que l'API refuserait.
    if (o.id === "quotas") return { ...o, hidden: (!vueOperateur && !gereLesOperateurs) || (Boolean(fiche) && !avecRegime) };
    return o;
  });
  const ongletsVisibles = onglets.filter((o) => !o.hidden).map((o) => o.id);
  const tabs = useFicheTabs({
    tabs: onglets,
    defaultId: ongletDemande || "informations",
    storageKey: "espoar.boa.ficheOperateur.onglet",
  });
  const selectionner = tabs.select;
  // Sur « Ma structure », l'adresse suit l'onglet (/ma-structure/<onglet>) :
  // les entrées du menu réduit rouvrent toujours le leur, Informations compris.
  const pathname = (props.location && props.location.pathname) || "";
  const surMaStructure = vueOperateur && pathname.startsWith(ACCUEIL_OPERATEUR);
  const historique = props.history;
  const choisirOnglet = useCallback(
    (id) => {
      selectionner(id);
      if (!surMaStructure || !historique || !historique.replace) return;
      const chemin = id === "informations" ? ACCUEIL_OPERATEUR : `${ACCUEIL_OPERATEUR}/${id}`;
      if (pathname !== chemin) historique.replace(chemin);
    },
    [selectionner, surMaStructure, historique, pathname]
  );
  useEffect(() => {
    if (ongletDemande) selectionner(ongletDemande);
    else if (surMaStructure) selectionner("informations");
  }, [ongletDemande, selectionner, surMaStructure]);

  if (structureEtrangere) {
    return (
      <div className="container-fluid px-5 my-5 fiche-page">
        <section className="list-card">
          <EmptyState
            icon="fa-building"
            title="Cette structure n'est pas la vôtre"
            hint="Votre compte ne voit que sa structure, ses sites et ses comptes."
            erreur
          />
          <p className="text-center pb-4 mb-0">
            <Link to={ACCUEIL_OPERATEUR} className="btn btn-ghost">
              Retour à ma structure
            </Link>
          </p>
        </section>
      </div>
    );
  }

  if (vueOperateur && !operateurId) {
    return (
      <div className="container-fluid px-5 my-5 fiche-page">
        <section className="list-card">
          <EmptyState
            icon="fa-building"
            title="Votre compte n'est rattaché à aucune structure"
            hint="Contactez la Caf pour faire corriger votre compte."
            erreur
          />
        </section>
      </div>
    );
  }

  const retour = vueOperateur ? null : (
    <p className="text-center pb-4 mb-0">
      <Link to="/operateurs" className="btn btn-ghost">
        Retour au répertoire des opérateurs
      </Link>
    </p>
  );

  if (introuvable) {
    return (
      <div className="container-fluid px-5 my-5 fiche-page">
        <section className="list-card">
          <EmptyState
            icon="fa-building"
            title="Cet opérateur n'existe pas"
            hint="Il a peut-être été supprimé."
            erreur
          />
          {retour}
        </section>
      </div>
    );
  }

  if (erreurChargement && !fiche) {
    return (
      <div className="container-fluid px-5 my-5 fiche-page">
        <section className="list-card">
          <EmptyState
            icon="fa-exclamation-triangle"
            title="La fiche de l'opérateur n'a pas pu être chargée"
            hint={erreurChargement}
            action={{ label: "Réessayer", onClick: chargerFiche }}
            erreur
          />
          {retour}
        </section>
      </div>
    );
  }

  if (!fiche) return <Loader />;

  const statut = STATUTS_OPERATEUR[fiche.statut] || {};

  const mettreEnService = () => {
    setActionEnCours(true);
    setRefusMiseEnService(null);
    mettreEnServiceOperateur(fiche.id).then(
      (ficheAJour) => {
        setActionEnCours(false);
        setFiche(ficheAJour);
        toast.success(`${ficheAJour.nom} est en service.`);
      },
      (erreur) => {
        setActionEnCours(false);
        setRefusMiseEnService(erreur.message || "La mise en service a été refusée.");
        // Le refus vient peut-être d'un compte désactivé entre-temps : la liste se remet à jour.
        if (erreur.code === "AUCUN_ADMINISTRATEUR_ACTIVE") chargerComptes();
      }
    );
  };

  const reactiver = () => {
    setActionEnCours(true);
    reactiverOperateur(fiche.id).then(
      (ficheAJour) => {
        setActionEnCours(false);
        setFiche(ficheAJour);
        ramenerFocusSurTitre();
        toast.success(
          ficheAJour.statut === "EN_PREPARATION"
            ? `${ficheAJour.nom} repasse en préparation : vérifiez ses sites et ses comptes avant de le remettre en service.`
            : `${ficheAJour.nom} est de nouveau actif.`
        );
      },
      (erreur) => {
        setActionEnCours(false);
        toast.error(erreur.message || "La réactivation a été refusée.");
      }
    );
  };

  const desactiver = () => {
    const motif = (modaleDesactivation.motif || "").trim();
    if (!motif) {
      setModaleDesactivation((m) => ({ ...m, erreurs: { motif: "Saisissez le motif de la désactivation." } }));
      return;
    }
    setActionEnCours(true);
    desactiverOperateur(fiche.id, motif).then(
      (ficheAJour) => {
        setActionEnCours(false);
        setFiche(ficheAJour);
        setModaleDesactivation(null);
        ramenerFocusSurTitre();
        toast.success(`${ficheAJour.nom} est désactivé.`);
        // La désactivation de l'opérateur désactive ses comptes.
        if ((ficheAJour.regimes || []).length) chargerComptes();
      },
      (erreur) => {
        setActionEnCours(false);
        if (erreur.champs) setModaleDesactivation((m) => ({ ...m, erreurs: erreur.champs }));
        else setModaleDesactivation((m) => ({ ...m, refus: erreur.message }));
      }
    );
  };

  const supprimer = () => {
    setActionEnCours(true);
    setModaleSuppression((m) => ({ ...m, refus: null, cite: false }));
    supprimerOperateur(fiche.id).then(
      () => {
        setActionEnCours(false);
        toast.success(`L'opérateur ${fiche.nom} a été supprimé.`);
        props.history.push("/operateurs");
      },
      (erreur) => {
        setActionEnCours(false);
        setModaleSuppression({
          refus: erreur.message || "La suppression a été refusée.",
          cite: erreur.code === "OPERATEUR_CITE",
        });
      }
    );
  };

  const actions = [];
  if (!vueOperateur) {
    if (fiche.statut === "INACTIF") {
      actions.push(
        <button
          key="reactiver"
          type="button"
          className="btn btn-ghost"
          onClick={reactiver}
          disabled={actionEnCours}
        >
          <i className="fa fa-play-circle mr-2" aria-hidden="true" />
          Réactiver l'opérateur
        </button>
      );
    } else {
      actions.push(
        <button
          key="desactiver"
          type="button"
          className="btn btn-ghost"
          onClick={() => setModaleDesactivation({ motif: "", erreurs: {}, refus: null })}
        >
          <i className="fa fa-pause-circle mr-2" aria-hidden="true" />
          Désactiver
        </button>
      );
      actions.push(
        <button
          key="supprimer"
          type="button"
          className="btn btn-ghost"
          onClick={() => setModaleSuppression({ refus: null, cite: false })}
        >
          <i className="fa fa-trash-alt mr-2" aria-hidden="true" />
          Supprimer
        </button>
      );
    }
  }

  const faits = [
    { id: "sigle", label: "Sigle", value: fiche.sigle, mono: true },
    { id: "email", label: "E-mail de contact", value: fiche.email },
    { id: "telephone", label: "Téléphone", value: fiche.telephone },
    {
      id: "adresse",
      label: "Siège",
      value: [fiche.adresse, [fiche.codePostal, fiche.ville].filter(Boolean).join(" ")]
        .filter(Boolean)
        .join(", "),
    },
    ...(avecRegime
      ? [
          {
            id: "delai",
            label: "Report d'un RDV",
            value: `${fiche.delaiReportRdvJours} jour${fiche.delaiReportRdvJours > 1 ? "s" : ""} minimum`,
          },
        ]
      : []),
  ];

  const eyebrow = vueOperateur
    ? "Ma structure"
    : avecRegime
    ? "Opérateur d'accompagnement"
    : "Opérateur du répertoire";

  return (
    <div className="container-fluid px-5 my-5 fiche-page fiche-operateur">
      <FicheHeader
        back={vueOperateur ? null : "/operateurs"}
        backLabel="Retour au répertoire des opérateurs"
        eyebrow={eyebrow}
        title={fiche.nom}
        titleRef={refTitre}
        pills={
          <>
            <Pill tone={statut.tone || "neutral"} icon={statut.icon}>
              {libelleStatut(fiche.statut)}
            </Pill>
            {(fiche.regimes || []).map((r) => (
              <Pill key={r} tone="neutral">
                {(REGIMES_ACCOMPAGNEMENT[r] || {}).titre || r}
              </Pill>
            ))}
          </>
        }
        actions={actions.length ? <div data-priv="crud_operateurs">{actions}</div> : null}
      >
        <div className="fiche-operateur__corps">
          <FactList facts={faits} emptyLabel="Non renseigné" />
          <EncartMiseEnService
            fiche={fiche}
            comptes={comptes}
            onMettreEnService={mettreEnService}
            onVoirOnglet={choisirOnglet}
            ongletsVisibles={ongletsVisibles}
            enCours={actionEnCours}
            refus={refusMiseEnService}
            lectureSeule={vueOperateur}
          />
        </div>
      </FicheHeader>

      <Tabs
        tabs={tabs.visibleTabs}
        activeId={tabs.activeId}
        onChange={choisirOnglet}
        idPrefix="fiche-operateur"
        ariaLabel="Sections de la fiche opérateur"
      />

      <TabPanel id="informations" activeId={tabs.activeId} idPrefix="fiche-operateur">
        <OngletInformations fiche={fiche} onFicheModifiee={ficheModifiee} lectureSeule={vueOperateur} />
      </TabPanel>

      <TabPanel id="sites" activeId={tabs.activeId} idPrefix="fiche-operateur">
        <OngletSites operateur={fiche} onFicheARafraichir={chargerFiche} lectureSeule={vueOperateur} />
      </TabPanel>

      {avecRegime && (
        <TabPanel id="utilisateurs" activeId={tabs.activeId} idPrefix="fiche-operateur">
          {comptes === null && erreurComptes ? (
            <section className="list-card">
              <EmptyState
                icon="fa-exclamation-triangle"
                title="Les comptes n'ont pas pu être chargés"
                hint={erreurComptes}
                action={{ label: "Réessayer", onClick: chargerComptes }}
                erreur
              />
            </section>
          ) : comptes === null ? (
            <p className="fiche-operateur__chargement">Chargement des comptes…</p>
          ) : (
            <OngletUtilisateurs
              operateur={fiche}
              comptes={comptes}
              editeur={utilisateur}
              onComptesModifies={chargerComptes}
            />
          )}
        </TabPanel>
      )}

      {avecRegime && (
        <TabPanel id="quotas" activeId={tabs.activeId} idPrefix="fiche-operateur">
          {/* Lu à l'ouverture de l'onglet ; un enregistrement relit la fiche pour l'étape de mise en service. */}
          {tabs.activeId === "quotas" && (
            <OngletQuotas operateur={fiche} onAllerOnglet={choisirOnglet} onQuotasModifies={chargerFiche} lectureSeule={vueOperateur} />
          )}
        </TabPanel>
      )}

      <ModaleLocale
        ouvert={!!modaleDesactivation}
        onFermer={() => setModaleDesactivation(null)}
        titre={`Désactiver ${fiche.nom} ?`}
        taille="etroite"
        idTitre="modale-desactivation"
        pied={
          modaleDesactivation && (
            <>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setModaleDesactivation(null)}
              >
                Annuler
              </button>
              <button
                type="submit"
                form="formulaire-desactivation"
                className="btn btn-primary"
                disabled={actionEnCours}
              >
                Désactiver l'opérateur
              </button>
            </>
          )
        }
      >
        {modaleDesactivation && (
          <form
            id="formulaire-desactivation"
            onSubmit={(e) => {
              e.preventDefault();
              desactiver();
            }}
            noValidate
          >
            <p>
              L'opérateur ne reçoit plus d'orientation et ses sites ne sont plus proposés dans les
              contrats d'engagement.{avecRegime ? " Les comptes de ses utilisateurs sont désactivés." : ""}{" "}
              Il reste visible dans le répertoire et peut être réactivé.
            </p>
            {modaleDesactivation.refus && (
              <div className="alert alert-danger" role="alert">
                {modaleDesactivation.refus}
              </div>
            )}
            <Control
              margin="my-0"
              label="Motif"
              name="motif"
              value={modaleDesactivation.motif}
              change={(e) => {
                // La valeur se lit avant le setState : l'événement React 17
                // est recyclé avant l'exécution de l'updater.
                const motif = e.target.value;
                setModaleDesactivation((m) => ({ ...m, motif, erreurs: {} }));
              }}
              error={modaleDesactivation.erreurs}
              required
              inputProps={{ autoComplete: "off", placeholder: "Exemple : fin de convention au 31/12/2026" }}
            />
            <p className="modale-operateur__aide">Le motif est conservé sur la fiche.</p>
          </form>
        )}
      </ModaleLocale>

      <ModaleLocale
        ouvert={!!modaleSuppression}
        onFermer={() => setModaleSuppression(null)}
        titre={`Supprimer ${fiche.nom} ?`}
        taille="etroite"
        idTitre="modale-suppression"
        pied={
          modaleSuppression && (
            <>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setModaleSuppression(null)}
              >
                Annuler
              </button>
              {modaleSuppression.cite ? (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    setModaleSuppression(null);
                    setModaleDesactivation({ motif: "", erreurs: {}, refus: null });
                  }}
                >
                  Désactiver à la place
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={supprimer}
                  disabled={actionEnCours}
                >
                  Supprimer l'opérateur
                </button>
              )}
            </>
          )
        }
      >
        {modaleSuppression && (
          <>
            <p className="mb-0">
              La structure, ses sites et leurs créneaux libres seront supprimés. Cette action est
              irréversible.
            </p>
            {modaleSuppression.refus && (
              <div className="alert alert-danger mt-3 mb-0" role="alert">
                {modaleSuppression.refus}
              </div>
            )}
          </>
        )}
      </ModaleLocale>
    </div>
  );
};

export default OperateurDetail;
